import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { getProductDescription, PRODUCT_WIDTHS } from "@/lib/productContent";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { t, lang } = useLang();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [descOpen, setDescOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getProduct(slug).then((p) => { setProduct(p); setActive(0); }).catch(() => setProduct(false));
  }, [slug]);

  if (product === false)
    return (
      <main className="pt-40 pb-40 text-center">
        <p className="font-display text-4xl mb-4 text-slate-900">{t("product.notFound")}</p>
        <Link to="/shop" className="font-mono-x text-xs uppercase tracking-widest text-[hsl(var(--accent))]">{t("product.backToShop")}</Link>
      </main>
    );
  if (!product) return <main className="min-h-screen" data-testid="product-loading" />;

  const specs = [
    [t("product.specs.airflow"), t("product.specs.airflowVal")],
    [t("product.specs.width"), PRODUCT_WIDTHS[product.slug] || product.width || "—"],
    [t("product.specs.length"), t("product.specs.lengthVal")],
    [t("product.specs.material"), t("product.specs.materialVal")],
  ];

  return (
    <main data-testid="product-page" className="pt-32 croc-bg text-foreground min-h-screen">
      <div className="mx-auto max-w-[1400px] px-6 md:px-8">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono-x text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> {t("product.back")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Gallery */}
          <div className="lg:sticky lg:top-28">
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-square rounded-3xl overflow-hidden border border-slate-200 bg-slate-50"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,85,0,0.08),transparent_60%)]" />
              <img data-testid="product-main-image" src={product.images?.[active]} alt={product.name} className="relative z-10 h-full w-full object-cover" />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={img}
                    data-testid={`thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-20 w-20 rounded-xl overflow-hidden border-2 transition-colors bg-slate-50 ${active === i ? "border-[hsl(var(--accent))]" : "border-slate-200"}`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="py-2">
            <div className="flex flex-wrap gap-2 mb-5">
              {product.tags?.map((tg) => (
                <span key={tg} className="font-mono-x text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--primary))] rounded-full px-3 py-1">{tg}</span>
              ))}
            </div>
            <h1 className="font-display font-extrabold text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9] text-[hsl(var(--primary))]">{product.name}</h1>
            <p className="mt-3 font-mono-x text-sm uppercase tracking-widest text-slate-400">{product.tagline}</p>

            <div className="mt-6 flex items-center gap-4">
              <span data-testid="product-price" className="font-display font-bold text-4xl text-[hsl(var(--accent))]">{fmt(product.price)}</span>
              {product.stock > 0 && product.stock <= 8 && (
                <span className="flex items-center gap-2 rounded-full border border-[hsl(var(--accent))] px-3 py-1">
                  <span className="h-2 w-2 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
                  <span className="font-mono-x text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">{t("product.onlyLeft", { n: product.stock })}</span>
                </span>
              )}
            </div>

            <div className="mt-8">
              <div className={`relative overflow-hidden transition-[max-height] duration-500 ease-in-out ${descOpen ? "max-h-[6000px]" : "max-h-44"}`}>
                <p data-testid="product-description" className="font-body text-base leading-relaxed text-slate-600 whitespace-pre-line">{getProductDescription(product.slug, lang, product.description)}</p>
                {!descOpen && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent" />}
              </div>
              <button
                data-testid="desc-toggle-btn"
                onClick={() => setDescOpen((v) => !v)}
                className="mt-4 inline-flex items-center gap-1.5 font-mono-x text-xs uppercase tracking-widest text-[hsl(var(--accent))] hover:text-slate-900 transition-colors"
              >
                {descOpen ? t("product.readLess") : t("product.readMore")}
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${descOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            <div className="mt-8 flex items-center gap-5">
              <div className="flex items-center gap-4 rounded-full border border-slate-300 px-4 py-3">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-slate-600 hover:text-slate-900 transition-colors"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="font-mono-x text-sm w-5 text-center">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="text-slate-600 hover:text-slate-900 transition-colors"><Plus className="h-4 w-4" /></button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                onClick={() => { addItem(product, qty); toast.success(t("product.addedToBag", { name: product.name, qty })); }}
                className="flex-1 rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-sm uppercase tracking-widest py-4 transition-all duration-300 hover:glow-orange"
              >
                {t("product.addToBag")}
              </button>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-slate-200 pt-8">
              {specs.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <span className="font-mono-x text-[10px] uppercase tracking-widest text-slate-400">{k}</span>
                  <span className="font-body text-sm text-slate-700 flex items-center gap-1"><Check className="h-3 w-3 text-[hsl(var(--accent))]" /> {v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="h-24" />
    </main>
  );
}
